import type { BarChatState } from './state-mapping';

/**
 * One color decision per bar state, expressed as a Tailwind `text-*` class —
 * every dot/ring/icon then just adds `bg-current` / `stroke-current` /
 * `fill-current` to pick it up, so there is exactly one place that decides
 * "asking is amber" instead of three maps that could quietly drift apart.
 *
 * Green/red are the app's `success`/`destructive` tokens and amber is the
 * canonical `amber-500` — both per the CLAUDE.md one-green/one-red/
 * canonical-amber rule, same tone `chat-light.tsx` already uses for the
 * identical states elsewhere in the app. `running`'s brand blue is the one
 * addition this design brings in (`--eddy-blue`, declared in `top-bar.css`,
 * scoped under the `.eddy-topbar` class every top-bar root carries).
 * `earlier` (the quiet tail) gets no state color at all — plain ink.
 */
export const STATE_COLOR_CLASS: Record<BarChatState, string> = {
  asking: 'text-amber-500',
  running: 'text-[var(--eddy-blue)]',
  ready: 'text-success',
  stalled: 'text-destructive',
  earlier: 'text-[var(--ink-3)]',
};

/** The board's group headings, in the same fixed order `groupBarChats`
 *  produces groups in. */
export const STATE_GROUP_LABEL: Record<BarChatState, string> = {
  asking: 'Asking you',
  running: 'Running',
  ready: 'Done',
  stalled: 'Stalled · needs a fix',
  earlier: 'Earlier',
};

/** The board header's count-pill wording — shorter than the group headings
 *  above (the heading may qualify the state, e.g. "Stalled · needs a fix"),
 *  and always the exact word the status cluster uses for that state in its
 *  hover tip and its accessible name, so the bar and the board never name one
 *  thing two ways. */
export const STATE_PILL_LABEL: Record<'asking' | 'running' | 'ready' | 'stalled', string> = {
  asking: 'asking you',
  running: 'running',
  ready: 'done',
  stalled: 'stalled',
};

/** The board header's per-state count pill — explicit per state (not
 *  derived from `STATE_COLOR_CLASS` + an opacity modifier on `currentColor`,
 *  which is a fragile combination) so every soft background is unambiguous.
 *  Only the four states the header ever shows a pill for. */
export const STATE_PILL_CLASS: Record<'asking' | 'running' | 'ready' | 'stalled', string> = {
  asking: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  running:
    'eddy-tb-pill--running border border-[var(--eddy-blue-line)] bg-[var(--eddy-blue-soft)] text-[var(--eddy-blue)]',
  ready: 'bg-success/15 text-success',
  stalled: 'bg-destructive/15 text-destructive',
};

/** The board row's hover action chip — matches `actionVerbFor` in
 *  `state-mapping.ts`: warn tint for "Answer", neutral otherwise. */
export function actionChipClass(state: BarChatState): string {
  if (state === 'asking') return 'bg-amber-500/10 text-amber-600 dark:text-amber-400';
  return 'border border-[var(--line)] bg-[var(--bg-1)] text-[var(--ink-1)]';
}
